import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  CircularProgress,
  Alert,
  ListItemButton,
} from '@mui/material';
import { AdditionalGear, RequestedAdditionalGear } from '../../models/AdditionalGear';
import { apiClient } from '../../publicApiClient';
import { gearIcons, getGearLabel } from '../../gears';
import { useTranslation } from 'react-i18next';

interface AdditionalGearStepProps {
  roomId: string;
  availableFromTime: string;
  durationInMinutes: number;
  selectedAdditionalGears: RequestedAdditionalGear[];
  onSelectionChange: (selected: RequestedAdditionalGear[]) => void;
  /** When provided by the parent stepper, skips a duplicate availability fetch. */
  availableGear?: AdditionalGear[];
}

export default function AdditionalGearStep({
  roomId,
  availableFromTime,
  durationInMinutes,
  selectedAdditionalGears,
  onSelectionChange,
  availableGear: availableGearProp,
}: AdditionalGearStepProps) {
  const { t } = useTranslation();
  const usingSharedGear = availableGearProp !== undefined;
  const [loading, setLoading] = useState(!usingSharedGear);
  const [gear, setGear] = useState<AdditionalGear[]>(availableGearProp ?? []);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (availableGearProp !== undefined) {
      setGear(availableGearProp);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    apiClient
      .getAvailableRoomGears(roomId, availableFromTime.replace('Z', ''), durationInMinutes)
      .then(setGear)
      .catch(() => setError(t('spaceCalendar.errorLoadingGear')))
      .finally(() => setLoading(false));
  }, [availableGearProp, roomId, availableFromTime, durationInMinutes, t]);

  const isSelected = (g: AdditionalGear) =>
    selectedAdditionalGears.some((s) => s.id === g.id && s.source === g.source);

  const handleToggle = (g: AdditionalGear) => {
    if (isSelected(g)) {
      onSelectionChange(selectedAdditionalGears.filter((s) => !(s.id === g.id && s.source === g.source)));
    } else {
      onSelectionChange([...selectedAdditionalGears, { id: g.id, source: g.source }]);
    }
  };

  if (loading) return <Box textAlign="center" py={4}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (gear.length === 0) return <Typography>{t('spaceCalendar.noGearAvailable')}</Typography>;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        fontWeight: 500, 
        px: { xs: 1, sm: 2 }, 
        py: 1, 
        color: 'text.secondary', 
        alignItems: 'center',
        fontSize: { xs: '0.75rem', sm: '0.875rem' }
      }}>
        <Box sx={{ width: { xs: 40, sm: 54 } }} />
        <Box sx={{ minWidth: { xs: 100, sm: 140 }, flex: 1 }}>{t('common.type')}</Box>
        <Box sx={{ flex: 2, pl: { xs: 1, sm: 2 }, display: { xs: 'none', sm: 'block' } }}>{t('common.details')}</Box>
        <Box sx={{ width: { xs: 80, sm: 100 }, textAlign: 'right' }}>{t('common.price')}</Box>
      </Box>
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <List>
          {gear.map((g) => {
          // Calculate price for the selected duration
          const price = (((g.pricePerH ?? 0) * (durationInMinutes / 60)) || 0).toFixed(2);
          return (
            <ListItem key={`${g.source}:${g.id}`} dense disablePadding>
              <ListItemButton onClick={() => handleToggle(g)}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={isSelected(g)}
                    tabIndex={-1}
                    disableRipple
                    inputProps={{ 'aria-labelledby': `gear-${g.id}` }}
                    sx={{ 
                      padding: { xs: 0.5, sm: 1 }
                    }}
                  />
                </ListItemIcon>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  {/* Type column */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    minWidth: { xs: 100, sm: 140 }, 
                    flex: 1,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {gearIcons[g.gearType]}
                    <span style={{ marginLeft: 8 }}>
                      {getGearLabel(g.gearType)}
                      {/* Show details in brackets on mobile */}
                      <span style={{ color: '#888', marginLeft: 4, fontWeight: 400 }}>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>
                          {g.details ? ` (${g.details})` : ''}
                        </Box>
                      </span>
                    </span>
                  </Box>
                  {/* Details column for sm and up */}
                  <Box
                    sx={{
                      color: 'text.secondary',
                      flex: 2,
                      pl: { xs: 1, sm: 2 },
                      display: { xs: 'none', sm: 'block' },
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    }}
                  >
                    {g.details}
                  </Box>
                  {/* Price column */}
                  <Box sx={{ 
                    width: { xs: 80, sm: 100 }, 
                    textAlign: 'right',
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {price} PLN
                  </Box>
                </Box>
              </ListItemButton>
            </ListItem>
          );
        })}
        </List>
      </Box>
    </Box>
  );
}
